import React, { useMemo } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import '../Dashboard.css';

const COLORS = ['#5e72e4', '#2dce89', '#11cdef', '#fb6340', '#f5365c', '#8965e0'];

const REGIONS_MASTER = ['Sierra', 'Costa', 'Oriente', 'Nacional'];

const STATIC_CIRCUIT_DATA = [
    { name: 'Sierra', value: 18 },
    { name: 'Costa', value: 14 },
    { name: 'Oriente', value: 6 }
];

// Helper for Firestore Timestamps
const safeDate = (date) => {
    if (!date) return null;
    if (typeof date.toDate === 'function') return date.toDate();
    const d = new Date(date);
    return isNaN(d.getTime()) ? null : d;
};

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value }) => {
    const RADIAN = Math.PI / 180;
    // Calculate position for the "Flag"
    const radius = outerRadius + 20; // Brought closer to fit
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Calculate text dimensions for the box
    const fontSize = 12;
    const labelText = `${name} (${value})`;
    const boxWidth = labelText.length * 7 + 20; // Approximate width based on text length
    const boxHeight = 24;

    // Determine position relative to center
    const isRight = x > cx;
    const isBottom = y > cy;

    // Adjust box position
    const boxX = isRight ? x : x - boxWidth;
    const boxY = isBottom ? y : y - boxHeight;

    return (
        <g>
            {/* Connecting Line */}
            <line
                x1={cx + (outerRadius + 5) * Math.cos(-midAngle * RADIAN)}
                y1={cy + (outerRadius + 5) * Math.sin(-midAngle * RADIAN)}
                x2={x}
                y2={y}
                stroke={COLORS[index % COLORS.length]}
                strokeWidth={2}
            />
            {/* Flag Box */}
            <rect
                x={boxX}
                y={boxY}
                width={boxWidth}
                height={boxHeight}
                fill={COLORS[index % COLORS.length]}
                rx={4}
                ry={4}
            />
            {/* Text */}
            <text
                x={boxX + boxWidth / 2}
                y={boxY + boxHeight / 2}
                fill="white"
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={fontSize}
                fontWeight="600"
            >
                {labelText}
            </text>
        </g>
    );
};

const AnalyticsPanel = ({ comerciales = [] }) => {

    const metrics = useMemo(() => {
        // Use ALL commercials for coverage/TV stats, not just 'online'
        // This allows user to see potential reach of their uploaded backlog.
        const relevantComerciales = comerciales || [];

        // Calculate "Active" count separately if needed for Trend, but for Coverage/TVs we use all.
        const activeCount = relevantComerciales.filter(c => (c.estado || '').toLowerCase() === 'online').length;

        let hasNationalVideo = false;
        const citiesPerRegion = { 'Sierra': new Set(), 'Costa': new Set(), 'Oriente': new Set(), 'Nacional': new Set() };

        relevantComerciales.forEach(c => { // Using relevantComerciales instead of activeComerciales
            // Check for National Scope
            if (c.segmentacion?.tipo === 'Nacional') {
                hasNationalVideo = true;
            }

            // Map cities to regions for the Split Chart
            const rawRegion = c.segmentacion?.region || 'Nacional';
            const regionsRaw = Array.isArray(rawRegion) ? rawRegion : [rawRegion];

            regionsRaw.forEach(regName => {
                const targetReg = REGIONS_MASTER.find(rm => rm.toLowerCase() === regName.toString().toLowerCase()) || 'Nacional';

                if (c.segmentacion?.ciudad) {
                    const cities = Array.isArray(c.segmentacion.ciudad) ? c.segmentacion.ciudad : [c.segmentacion.ciudad];
                    cities.forEach(city => {
                        const cityName = city.toString().trim();
                        if (cityName) {
                            // Add to specific region set
                            if (citiesPerRegion[targetReg]) {
                                citiesPerRegion[targetReg].add(cityName);
                            }
                        }
                    });
                }
            });
        });

        // Calculate TV Breakdown
        let tvPieData = [];
        let totalTvs = 0;
        // Total 'Scoper' cities (from all videos)
        let totalScopeCities = 0;

        if (hasNationalVideo) {
            // Full Circuit Breakdown - Sierra, Costa, Oriente
            tvPieData = STATIC_CIRCUIT_DATA.map(d => ({
                name: d.name,
                value: d.value * 12
            }));
            totalTvs = 38 * 12; // 456
            totalScopeCities = 38; // Show full coverage
            // totalScopeCities = 38;
        } else {
            // Specific Cities Breakdown from uploaded videos
            tvPieData = ['Sierra', 'Costa', 'Oriente'].map(reg => {
                const count = citiesPerRegion[reg]?.size || 0;
                return { name: reg, value: count * 12 };
            }).filter(d => d.value > 0);

            // Unique cities count across all regions
            const allUniqueCities = new Set([...citiesPerRegion['Sierra'], ...citiesPerRegion['Costa'], ...citiesPerRegion['Oriente'], ...citiesPerRegion['Nacional']]);
            totalScopeCities = allUniqueCities.size;
            totalTvs = totalScopeCities * 12;
        }

        return { totalTvs, totalActive: totalScopeCities, tvPieData, activeCount }; // activeCount unused but good to keep
    }, [comerciales]);

    const trendData = useMemo(() => {
        const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'].map(m => ({
            name: m,
            total: 0,
            active: 0
        }));
        const currentYear = new Date().getFullYear();

        (comerciales || []).forEach(c => {
            const d = safeDate(c.fechaRegistro);
            if (d && d.getFullYear() === currentYear) {
                const monthIdx = d.getMonth();
                months[monthIdx].total += 1;
                if ((c.estado || '').toLowerCase() === 'online') {
                    months[monthIdx].active += 1;
                }
            }
        });
        return months;
    }, [comerciales]);

    return (
        <div className="analytics-panel-elite">
            <div className="charts-container-elite horizontal-layout">

                {/* 1. TREND CHART */}
                <div className="chart-wrapper white-card card-third">
                    <div className="chart-header">
                        <h4>📈 Tendencia ({trendData.reduce((a, b) => a + b.total, 0)})</h4>
                    </div>
                    <div className="chart-content-area">
                        <ResponsiveContainer>
                            <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#5e72e4" stopOpacity={0.2} /><stop offset="95%" stopColor="#5e72e4" stopOpacity={0} /></linearGradient>
                                    <linearGradient id="colorActive" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2dce89" stopOpacity={0.2} /><stop offset="95%" stopColor="#2dce89" stopOpacity={0} /></linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f5f5f5" />
                                <XAxis
                                    dataKey="name"
                                    stroke="#adb5bd"
                                    fontSize={10}
                                    tickLine={false}
                                    axisLine={false}
                                    interval={0}
                                    padding={{ left: 10, right: 10 }}
                                />
                                <YAxis
                                    width={25}
                                    stroke="#adb5bd"
                                    fontSize={9}
                                    tickLine={false}
                                    axisLine={false}
                                    domain={[0, dataMax => Math.max(dataMax + 2, 5)]}
                                />
                                <Tooltip />
                                <Area type="monotone" dataKey="total" name="Subidos" stroke="#5e72e4" strokeWidth={2} fill="url(#colorTotal)" />
                                <Area type="monotone" dataKey="active" name="Activos" stroke="#2dce89" strokeWidth={2} fill="url(#colorActive)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* 2. CITIES DONUT (STATIC 38) */}
                <div className="chart-wrapper white-card card-third">
                    <div className="chart-header">
                        <h4>🗺️ Cobertura: {metrics.totalActive}/38 Ciudades</h4>
                    </div>
                    <div className="chart-content-area donut-height-fix">
                        <ResponsiveContainer>
                            <PieChart margin={{ top: 50, right: 30, bottom: 30, left: 30 }}>
                                <Pie
                                    data={STATIC_CIRCUIT_DATA}
                                    cx="50%" cy="50%" innerRadius={40} outerRadius={60}
                                    paddingAngle={5} dataKey="value" cornerRadius={8}
                                    label={renderCustomizedLabel}
                                >
                                    {STATIC_CIRCUIT_DATA.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />)}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="donut-center-label">
                            <span className="donut-number dark">38</span>
                            <span className="donut-text dark">Ciudades Total</span>
                        </div>
                    </div>
                </div>

                {/* 3. TVS DONUT (Calculated Sum) */}
                <div className="chart-wrapper white-card card-third">
                    <div className="chart-header">
                        <h4>📺 Pantallas On: {metrics.totalTvs}</h4>
                    </div>
                    <div className="chart-content-area donut-height-fix">
                        <ResponsiveContainer>
                            <PieChart margin={{ top: 50, right: 30, bottom: 30, left: 30 }}>
                                <Pie
                                    data={metrics.tvPieData && metrics.tvPieData.length > 0 ? metrics.tvPieData : [{ name: 'Sin Videos', value: 1 }]}
                                    cx="50%" cy="50%" innerRadius={40} outerRadius={60}
                                    paddingAngle={5} dataKey="value" cornerRadius={8}
                                    label={renderCustomizedLabel}
                                >
                                    {metrics.tvPieData && metrics.tvPieData.length > 0
                                        ? metrics.tvPieData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />)
                                        : <Cell fill="#f0f2f5" stroke="none" />
                                    }
                                </Pie>
                                <Tooltip formatter={(value, name) => [value, name]} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="donut-center-label">
                            <span className="donut-number dark">{metrics.totalTvs}</span>
                            <span className="donut-text dark">TVs On</span>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default React.memo(AnalyticsPanel);
